//* 로그인 화면의 [문의 남기기] — 로그인을 못 한 사람이 로그인 없이 남기는 문의 (Slack 으로 감)
import { FC, useCallback, useState } from 'react';

import { BugoBasicButton } from '@service/bugo/components/BugoBasicButton';
import { createLoginHelpEnquiry } from '@shared/api/enquiry/enquiry.controller';
import {
  LOGIN_HELP_PRESET_LABEL,
  LoginHelpPreset,
} from '@shared/api/enquiry/enquiry.interface';
import { errorMessageOf } from '@shared/utils/serverErrorMessage.util';

/**
 * 백엔드 스펙: smartbugo-api/docs/login-help-enquiry.md
 *
 * 전화번호를 화면에 두지 않는다 — 번호를 아는 팀장들은 어차피 전화하고, 나머지는 이 문의를
 * 남기면 Slack 으로 바로 온다. 종류는 버튼으로 골라 한 번 눌러 보낼 수 있게 한다.
 * 제목은 서버가 preset 으로 정하므로 여기서는 보내지 않는다.
 */

const PRESETS: LoginHelpPreset[] = [
  LoginHelpPreset.ForgotUsername,
  LoginHelpPreset.ForgotPassword,
  LoginHelpPreset.DeviceLocked,
  LoginHelpPreset.PhoneChanged,
  LoginHelpPreset.Other,
];

const NAME_MAX = 30;
const CONTENT_MAX = 500;

const inputClass =
  'h-11 w-full rounded-lg border border-gray-300 bg-[#F9FAFB] px-4 text-15 theme-bg-1 placeholder:font-normal placeholder:text-gray-400';

interface Props {
  /** 실패 상황에 맞춰 미리 골라 두는 종류 (잠김 → DeviceLocked 등) */
  initialPreset?: LoginHelpPreset;
  /** 로그인 폼에 입력돼 있던 아이디 — 관리자가 잠금 해제 페이지에서 바로 찾는다 */
  username?: string;
  /** 고객 화면(화환 스토어 로그인 등)에서 열린 폼 — 기기 헤더를 빼고 보낸다 */
  customerSurface?: boolean;
  onBack: () => void;
}

export const LoginHelpForm: FC<Props> = function LoginHelpForm({
  initialPreset = LoginHelpPreset.ForgotPassword,
  username,
  customerSurface = false,
  onBack,
}: Props) {
  const [preset, setPreset] = useState<LoginHelpPreset>(initialPreset);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneTitle, setDoneTitle] = useState<string | null>(null);

  const contentRequired = preset === LoginHelpPreset.Other;

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    if (!name.trim()) {
      setError('성함을 입력해 주세요.');
      return;
    }
    if (phoneNumber.replace(/\D/g, '').length < 10) {
      setError('휴대폰 번호를 입력해 주세요.');
      return;
    }
    if (contentRequired && !content.trim()) {
      setError('문의 내용을 입력해 주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createLoginHelpEnquiry(
        {
          preset,
          name: name.trim(),
          phoneNumber: phoneNumber.trim(),
          content: content.trim() || undefined,
          username: username?.trim() || undefined,
        },
        { customerSurface },
      );
      setDoneTitle(created.title);
    } catch (err) {
      //* 400(검증)·429(도배) 모두 서버가 사용자에게 보여줄 문장을 준다
      setError(errorMessageOf(err, '접수에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setSubmitting(false);
    }
  }, [content, contentRequired, customerSurface, name, phoneNumber, preset, submitting, username]);

  if (doneTitle) {
    return (
      <div className="space-y-4 text-center">
        <h3 className="text-16 font-bold theme-text-1">문의가 접수되었습니다</h3>
        <p className="break-keep text-14 leading-5 text-gray-500">
          「{doneTitle}」
          <br />
          확인 후 남겨주신 번호로 문자 드리겠습니다.
        </p>
        <BugoBasicButton
          type="button"
          className="rounded-lg theme-bg-main"
          onClick={onBack}
          text="로그인 화면으로"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-16 font-bold theme-text-1">로그인 문의</h3>
        <p className="mt-1 break-keep text-14 leading-5 text-gray-500">
          어떤 문제인지 고르고 연락처를 남겨주세요. 확인 후 문자로 안내드립니다.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((item) => {
          const selected = item === preset;
          return (
            <button
              key={item}
              type="button"
              onClick={() => {
                setPreset(item);
                setError(null);
              }}
              className={`rounded-full border px-3 py-1.5 text-13 ${
                selected
                  ? 'border-gray-800 bg-gray-800 font-bold text-white'
                  : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              {LOGIN_HELP_PRESET_LABEL[item]}
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        <input
          type="text"
          className={inputClass}
          placeholder="성함"
          maxLength={NAME_MAX}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
        />
        <input
          type="tel"
          inputMode="numeric"
          className={inputClass}
          placeholder="휴대폰 번호 (문자 안내용)"
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value.replace(/[^0-9-]/g, '').slice(0, 13));
            setError(null);
          }}
        />
        <textarea
          className={`${inputClass} h-24 resize-none py-3`}
          placeholder={contentRequired ? '문의 내용 (필수)' : '문의 내용 (선택)'}
          maxLength={CONTENT_MAX}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            setError(null);
          }}
        />
      </div>

      {error && <p className="text-14 text-red-500">{error}</p>}

      <BugoBasicButton
        type="button"
        className="rounded-lg theme-bg-main disabled:opacity-40"
        onClick={handleSubmit}
        disabled={submitting}
        text={submitting ? '접수 중...' : '문의 남기기'}
      />
      <button
        type="button"
        className="w-full py-1 text-14 text-gray-500 underline"
        onClick={onBack}
      >
        로그인 화면으로
      </button>
    </div>
  );
};
